import * as authApi from "@/services/auth";
import { getTestRoleId, getRolePrefix, clearTestRole } from "@/helpers/roleHelper";

export const state = {
  user: JSON.parse(localStorage.getItem("user")) || {
    name: "",
    email: "",
    role: "",
    roleId: null,
    profilePicture: "",
    id: null,
  },
  accessToken: localStorage.getItem("accessToken") || null,
  refreshToken: localStorage.getItem("refreshToken") || null,
  expiresAt: localStorage.getItem("expiresAt") || null,
};

export const mutations = {
  SET_USER(state, user) {
    state.user = {
      name: user.name || user.fullName || "",
      email: user.email || "",
      role: user.role || "",
      roleId: user.roleId || null,
      profilePicture: user.profilePicture || "",
      id: user.id || null,
    };
    localStorage.setItem("user", JSON.stringify(state.user));
  },
  SET_TOKENS(state, { accessToken, refreshToken, expiresAt }) {
    state.accessToken = accessToken;
    state.refreshToken = refreshToken;
    state.expiresAt = expiresAt;
    localStorage.setItem("accessToken", accessToken);
    localStorage.setItem("refreshToken", refreshToken);
    localStorage.setItem("expiresAt", expiresAt);
  },
  CLEAR_USER(state) {
    state.user = {
      name: "",
      email: "",
      role: "",
      roleId: null,
      profilePicture: "",
    };
    state.accessToken = null;
    state.refreshToken = null;
    state.expiresAt = null;
    localStorage.clear();
    clearTestRole();
  },
};

export const actions = {
  async login({ commit }, credentials) {
    try {
      const response = await authApi.login(credentials);

      if (response.success) {
        const { accessToken, refreshToken, ...user } = response.data;
        const expiresAt = Date.now() + 30 * 24 * 60 * 60 * 1000;

        // Untuk testing lokal, gunakan roleId dari helper
        const testRoleId = getTestRoleId();
        const roleId = testRoleId || user.roleId;

        const userData = {
          name: user.fullName || user.name || "",
          email: user.email || "",
          role: user.role || "",
          roleId: roleId,
          profilePicture: user.profilePicture || "",
          id: user.id || null, // Ensure ID is passed
        };

        commit("SET_USER", userData);
        commit("SET_TOKENS", { accessToken, refreshToken, expiresAt });

        return { success: true, data: { accessToken } };
      } else {
        return { success: false, message: response.message };
      }
    } catch (error) {
      throw new Error(error.message || "Login failed");
    }
  },

  async fetchUserProfile({ commit }) {
    try {
      const response = await authApi.getProfile();
      if (response.success) {
        const userData = response.data;
        const testRoleId = getTestRoleId();
        const roleId = testRoleId || userData.roleId;

        commit("SET_USER", {
          name: userData.fullName || userData.name || "",
          email: userData.email || "",
          role: userData.role || "",
          roleId: roleId,
          profilePicture: userData.profilePicture || "",
          id: userData.id || null, // Ensure ID is passed
        });
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
    }
  },

  async forgotPassword(_, email) {
    try {
      const response = await authApi.forgotPassword(email);
      return response;
    } catch (error) {
      throw new Error(error.message || "Forgot password failed");
    }
  },

  logout({ commit }) {
    commit("CLEAR_USER");
    clearTestRole();
    return Promise.resolve();
  },

  checkSession({ commit }) {
    const expiresAt = localStorage.getItem("expiresAt");

    if (expiresAt && Date.now() > expiresAt) {
      commit("CLEAR_USER");
    }
  },
};

export const getters = {
  isAuthenticated: (state) =>
    !!state.accessToken && Date.now() < state.expiresAt,
  currentUser: (state) =>
    state.user || {
      name: "",
      email: "",
      role: "",
      roleId: null,
      profilePicture: "",
    },
  currentRoleId: (state) => state.user?.roleId,
  currentUserRolePrefix: (state) => {
    const roleId = state.user ? state.user.roleId : null;
    return getRolePrefix(roleId);
  },
  currentUserId: (state) => (state.user ? state.user.id : null),
  isLoggedIn: (state) => !!state.accessToken,
};
